import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Grid,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import ReminderMessageList from './ReminderMessageList';

const ReminderTimeConfig = ({
  reminderTime,
  onReminderTimeChange,
  templates,
  communicationEndpoints,
  channel,
  onDelete,
  index,
  useRelativeSchedule,
}) => {

  // Initialize relative schedule values when switching to relative schedule
  useEffect(() => {
    
    if (useRelativeSchedule && !reminderTime.days_before_relative && !reminderTime.hours_before && !reminderTime.minutes_before) {
      onReminderTimeChange({
        ...reminderTime,
        days_before_relative: 0,
        hours_before: 0,
        minutes_before: 0,
      });
    }
  }, [useRelativeSchedule]);

  const handleTimeChange = (field) => (event) => {
    
    
    const value = event.target.type === 'number' ? parseInt(event.target.value) || 0 : event.target.value;
    
    // When switching between relative and absolute schedules, clear the other type's values
    const updatedTime = {
      ...reminderTime,
      [field]: value,
    };

    if (useRelativeSchedule) {
      // Clear absolute schedule values
      updatedTime.days_before = null;
      updatedTime.time = null;
    } else {
      // Clear relative schedule values
      updatedTime.days_before_relative = null;
      updatedTime.hours_before = null;
      updatedTime.minutes_before = null;
    }

    onReminderTimeChange(updatedTime);
  };

  const validateRelativeSchedule = () => {
    if (!useRelativeSchedule) return true;
    const isValid = reminderTime.days_before_relative > 0 || reminderTime.hours_before > 0 || reminderTime.minutes_before > 0;
    return isValid;
  };


  return (
    <Paper sx={{ p: 3, mb: 4, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Reminder {index + 1}
        </Typography>
        <IconButton onClick={onDelete} color="error" size="small">
          <DeleteIcon />
        </IconButton>
      </Box>
      
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {useRelativeSchedule ? (
          <>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Days Before"
                value={reminderTime.days_before_relative || 0}
                onChange={handleTimeChange('days_before_relative')}
                InputProps={{ inputProps: { min: 0 } }}
                error={!validateRelativeSchedule()}
                helperText={!validateRelativeSchedule() ? "At least one time value is required" : ""}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Hours Before"
                value={reminderTime.hours_before || 0}
                onChange={handleTimeChange('hours_before')}
                InputProps={{ inputProps: { min: 0 } }}
                error={!validateRelativeSchedule()}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Minutes Before"
                value={reminderTime.minutes_before || 0}
                onChange={handleTimeChange('minutes_before')}
                InputProps={{ inputProps: { min: 0 } }}
                error={!validateRelativeSchedule()}
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                required
                type="number"
                label="Days Before"
                value={reminderTime.days_before || ''}
                onChange={handleTimeChange('days_before')}
                InputProps={{ inputProps: { min: 0 } }}
                error={!reminderTime.days_before}
                helperText={!reminderTime.days_before ? "Days before is required" : ""}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                required
                type="time"
                label="Time"
                value={reminderTime.time || ''}
                onChange={handleTimeChange('time')}
                InputLabelProps={{ shrink: true }}
                error={!reminderTime.time}
                helperText={!reminderTime.time ? "Time is required" : ""}
              />
            </Grid>
          </>
        )}
      </Grid>

      <ReminderMessageList
        messages={reminderTime.messages}
        onMessagesChange={(newMessages) => onReminderTimeChange({
          ...reminderTime,
          messages: newMessages,
        })}
        templates={templates}
        communicationEndpoints={communicationEndpoints}
        channel={channel}
        timeIndex={index}
      />
    </Paper>
  );
};

ReminderTimeConfig.propTypes = {
  reminderTime: PropTypes.shape({
    days_before: PropTypes.number,
    time: PropTypes.string,
    days_before_relative: PropTypes.number,
    hours_before: PropTypes.number,
    minutes_before: PropTypes.number,
    messages: PropTypes.arrayOf(
      PropTypes.shape({
        template: PropTypes.string,
        email_config: PropTypes.object,
        sms_config: PropTypes.object,
        voice_config: PropTypes.object,
        chat_config: PropTypes.object,
      })
    ),
  }).isRequired,
  onReminderTimeChange: PropTypes.func.isRequired,
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  communicationEndpoints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  channel: PropTypes.oneOf(['email', 'sms', 'voice', 'chat']).isRequired,
  onDelete: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  useRelativeSchedule: PropTypes.bool.isRequired,
};

export default ReminderTimeConfig; 